/**
 * React hooks for feature flags
 * Provider-agnostic hooks that work with any feature flag adapter
 */

import { useFeatureFlagContext as useFeatureFlagManager } from '../components/FeatureFlagProvider'
import { FeatureFlagVariant } from '../types'

/**
 * Hook to check if a feature flag is enabled
 */
export const useFeatureFlag = (flagName: string): boolean => {
	try {
		const manager = useFeatureFlagManager()
		return manager.isEnabled(flagName)
	} catch {
		return false
	}
}

/**
 * Hook to get feature flag variant
 */
export const useFeatureFlagVariant = (
	flagName: string
): FeatureFlagVariant => {
	try {
		const manager = useFeatureFlagManager()
		return manager.getVariant(flagName)
	} catch {
		return { enabled: false, name: 'control', payload: {} }
	}
}

/**
 * Hook to get flags status (ready state and errors)
 */
export const useFeatureFlagsStatus = () => {
	try {
		const manager = useFeatureFlagManager()
		return manager.getFlagsStatus()
	} catch {
		return { flagsReady: false, flagsError: undefined }
	}
}

/**
 * Hook to get all feature flag functionality
 */
export const useFeatureFlags = (flagName?: string) => {
	const isEnabled = flagName ? useFeatureFlag(flagName) : false
	const variant = flagName
		? useFeatureFlagVariant(flagName)
		: { enabled: false, name: 'control', payload: {} }
	const { flagsReady, flagsError } = useFeatureFlagsStatus()

	return {
		isEnabled: Boolean(isEnabled),
		variant,
		flagsReady,
		flagsError
	}
}

/**
 * Hook to get automatic context from stores
 */
export { useAutomaticFeatureFlagContext } from './useFeatureFlagContext'

